import React from "react";
import { Navigate } from "react-router-dom";
import { useSubscription } from "..//Hooks/useSubscription";

interface IProtectedFeatureRouteProps {
  feature: string;
  children: React.ReactNode;
  fallbackPath?: string;
}

/**
 * Wraps a route element and only renders it if the organization's current
 * plan has the given feature enabled. This is a UX convenience layer only —
 * the backend requireFeature(feature) middleware remains the authoritative
 * enforcement for the underlying APIs.
 *
 * Authentication and subscription authorization are separate concerns: this
 * component assumes it only ever renders inside your existing authenticated
 * route tree (i.e. behind whatever already checks the user is logged in) —
 * it does not duplicate or replace that check.
 *
 * Usage inside your existing React Router config:
 *
 *   <Route
 *     path="/inventory"
 *     element={
 *       <ProtectedFeatureRoute feature="inventory">
 *         <Inventory />
 *       </ProtectedFeatureRoute>
 *     }
 *   />
 */
const ProtectedFeatureRoute: React.FC<IProtectedFeatureRouteProps> = ({
  feature,
  children,
  fallbackPath = "/dashboard/subscription-required",
}) => {
  const { hasFeature, loading, isActive } = useSubscription();

  if (loading) {
    return <div style={{ padding: 24, textAlign: "center" }}>Checking subscription…</div>;
  }

  if (!isActive || !hasFeature(feature)) {
    return <Navigate to={fallbackPath} replace state={{ blockedFeature: feature }} />;
  }

  return <>{children}</>;
};

export default ProtectedFeatureRoute;